import React, { useState, useEffect } from 'react';
import {
  BuildingStorefrontIcon,
  EyeIcon,
  EyeSlashIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { getElloStatus, getElloEnabledUsers, sendElloNeedHelp } from '../../../api/offeringApi';
import { errorToast, successToast } from '../../../utils/helper';
import Table from '../../../component/common/Table';
import SearchInput from '../../../component/common/Input/SearchInput';
import SelectMenu from '../../../component/common/SelectMenu';
import Pagination from '../../../component/common/Pagination/Pagination';
import { PER_PAGE } from '../../../utils/constants';
import GourmetSocietyAddUsersModal from './GourmetSocietyAddUsersModal';
import { TableCard } from '../../../component/common/designSystem';

const IS_USED_OPTIONS = [
  { name: 'All Users', value: '' },
  { name: 'Used', value: 'true' },
  { name: 'Unused', value: 'false' }
];

const MarketplaceGourmetSociety = () => {
  const [statusLoading, setStatusLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [totalCoupons, setTotalCoupons] = useState(null);
  const [totalAssigned, setTotalAssigned] = useState(null);
  const [totalRedeemed, setTotalRedeemed] = useState(null);
  const [enabledUserCount, setEnabledUserCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIsUsed, setSelectedIsUsed] = useState(IS_USED_OPTIONS[0]);
  const [visibleCodes, setVisibleCodes] = useState({});
  const [helpModal, setHelpModal] = useState({ open: false, userId: null, userName: '' });
  const [helpDescription, setHelpDescription] = useState('');
  const [helpLoading, setHelpLoading] = useState(false);

  const fetchStatus = async () => {
    setStatusLoading(true);
    try {
      const response = await getElloStatus();
      const data = response?.data || {};
      setEnabled(data.elloOfferEnabled ?? false);
      setTotalCoupons(data.totalCoupons != null ? Number(data.totalCoupons) : null);
      setTotalAssigned(
        data.totalAssignedCoupons != null ? Number(data.totalAssignedCoupons) : null
      );
      setTotalRedeemed(data.totalRedeemed != null ? Number(data.totalRedeemed) : null);
      setEnabledUserCount(Number(data.totalAssignedCoupons) || 0);
    } catch {
      setEnabled(false);
      setTotalCoupons(null);
      setTotalAssigned(null);
      setTotalRedeemed(null);
      setEnabledUserCount(0);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch enabled users whenever filters change (only when enabled)
  useEffect(() => {
    if (!enabled) return;

    fetchUsers();
  }, [enabled, currentPage, selectedPerPage, debouncedSearch, selectedIsUsed]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await getElloEnabledUsers({
        page: currentPage,
        perPage: selectedPerPage?.value || 10,
        search: debouncedSearch || undefined,
        isUsed: selectedIsUsed?.value || undefined
      });

      const data = Array.isArray(response?.data) ? response.data : [];
      const meta = response?.meta || response;
      setUsers(data);
      setVisibleCodes({});
      setTotalCount(Number(meta?.totalRecords) || data.length);
    } catch {
      setUsers([]);
      setTotalCount(0);
    } finally {
      setUsersLoading(false);
    }
  };
  const openHelpModal = (userId, userName) => {
    setHelpDescription('');
    setHelpModal({ open: true, userId, userName });
  };

  const closeHelpModal = () => {
    setHelpModal({ open: false, userId: null, userName: '' });
    setHelpDescription('');
  };

  const handleSendHelp = async () => {
    if (!helpDescription.trim()) return;
    setHelpLoading(true);
    try {
      const response = await sendElloNeedHelp({
        userId: helpModal.userId,
        message: helpDescription.trim()
      });
      if (response?.meta?.code === 1) {
        successToast(response?.meta?.message || 'Help request sent successfully');
        closeHelpModal();
      } else {
        errorToast(response?.meta?.message || 'Failed to send help request');
      }
    } catch (error) {
      errorToast(error?.message || 'Failed to send help request');
    } finally {
      setHelpLoading(false);
    }
  };

  const toggleCodeVisibility = (id) => setVisibleCodes((prev) => ({ ...prev, [id]: !prev[id] }));

  const maskCode = (code = '') => {
    if (!code || code.length <= 4) return code;
    return `${'*'.repeat(code.length - 4)}${code.slice(-4)}`;
  };

  const getInitials = (name = '') =>
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');

  const tableData = users.map((user) => {
    const offer = user.elloOffer || {};
    const isVisible = !!visibleCodes[user._id];
    const offerCode = offer.offerCode || '';
    const initials = getInitials(user.name);
    return {
      id: user._id,
      profile: (
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-800 text-xs font-semibold text-white dark:bg-gray-700">
            {initials || '?'}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
              {user.name || 'N/A'}
            </p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
              {user.email || 'N/A'}
            </p>
          </div>
        </div>
      ),
      offerCode: (
        <span className="inline-flex items-center gap-2">
          <span className="font-mono text-sm">{isVisible ? offerCode : maskCode(offerCode)}</span>
          <button
            type="button"
            onClick={() => toggleCodeVisibility(user._id)}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
            aria-label={isVisible ? 'Hide offer code' : 'Show offer code'}
          >
            {isVisible ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
        </span>
      ),
      voucherStatus: offer.voucherStatus || 'N/A',
      redeemStatus: offer.redeemStatus || 'N/A',
      emailSentAt: offer.emailSentAt ? new Date(offer.emailSentAt).toLocaleString() : 'N/A',
      ctaClickedAt: offer.ctaClickedAt ? new Date(offer.ctaClickedAt).toLocaleString() : 'N/A',
      purchaseAt: offer.purchaseAt ? new Date(offer.purchaseAt).toLocaleString() : 'N/A',
      redeemedAt: offer.redeemedAt ? new Date(offer.redeemedAt).toLocaleString() : 'N/A',
      expireAt: offer.expireAt ? new Date(offer.expireAt).toLocaleString() : 'N/A',
      needHelp: (
        <button
          type="button"
          onClick={() => openHelpModal(user._id, user.name || '')}
          className="px-3 py-1.5 rounded-full text-xs font-medium border border-shoorah-primary text-shoorah-primary hover:bg-shoorah-primary hover:text-white transition-colors whitespace-nowrap"
        >
          Need Help
        </button>
      )
    };
  });

  const columns = [
    {
      title: 'Profile',
      key: 'profile',
      sortable: false,
      type: 'customTemplate',
      align: 'left',
      customTemplateRender: (rowData) => rowData.profile
    },
    { title: 'Purchased At', key: 'purchaseAt', sortable: false, type: 'text', align: 'left' },

    // { title: 'Offer Code', key: 'offerCode', sortable: false, type: 'text', align: 'left' },
    {
      title: 'Coupon Status',
      key: 'voucherStatus',
      sortable: false,
      type: 'text',
      align: 'left'
    },
    { title: 'Expires At', key: 'expireAt', sortable: false, type: 'text', align: 'left' },

    { title: 'Redeem Status', key: 'redeemStatus', sortable: false, type: 'text', align: 'left' },
    // { title: 'Email Sent At', key: 'emailSentAt', sortable: false, type: 'text', align: 'left' },
    // { title: 'CTA Clicked At', key: 'ctaClickedAt', sortable: false, type: 'text', align: 'left' },
    { title: 'Redeemed At', key: 'redeemedAt', sortable: false, type: 'text', align: 'left' },
    {
      title: 'Action',
      key: 'needHelp',
      sortable: false,
      type: 'customTemplate',
      align: 'left',
      customTemplateRender: (rowData) => rowData.needHelp
    }
  ];

  if (statusLoading) {
    return (
      <div className="mt-6 bg-white dark:bg-shoorah-darkBgTabColor shadow rounded-lg flex items-center justify-center py-16">
        <svg
          className="animate-spin h-10 w-10 text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="mt-6 bg-white dark:bg-shoorah-darkBgTabColor shadow rounded-lg">
        <div className="px-4 py-8 sm:px-6 lg:px-8 flex flex-col items-center justify-center py-16">
          <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-5">
            <BuildingStorefrontIcon className="w-10 h-10 text-red-500 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Gourmet Society</h2>
          <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 mb-5">
            Not Enabled
          </span>
          <p className="text-gray-500 dark:text-gray-400 text-center max-w-md leading-relaxed">
            Gourmet Society is not yet enabled for your company. Please contact shoorah on{' '}
            <a href="mailto:info@shoorah.io" className="text-shoorah-primary">
              info@shoorah.io
            </a>
            &nbsp;to get access to Ello offering. checkout more details on{' '}
            <a
              href="https://www.gourmetsociety.co.uk/"
              className="text-shoorah-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Gourmet Society
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Stat cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {[
          { label: 'Total Coupons', value: totalCoupons },
          { label: 'Total Redeemed', value: totalRedeemed },
          { label: 'Total Assigned Coupons', value: totalAssigned }
        ].map(({ label, value }) => (
          <div
            key={label}
            className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-200 dark:border-gray-600 rounded-xl p-5 flex items-center gap-4 shadow-sm"
          >
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
              <svg
                className="h-5 w-5 text-shoorah-primary"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.8}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                {value != null ? value : <span className="text-gray-400 text-lg">—</span>}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg p-6">
        {/* Add Users button */}

        {/* Users table */}
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-3">
          Gourmet Society Users
        </h3>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <SearchInput
              id="gourmetUserSearch"
              name="gourmetUserSearch"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email"
            />
            {/* <div className="w-full sm:w-[160px]">
              <SelectMenu
                menuList={IS_USED_OPTIONS}
                showLabel={false}
                defaultSelected={selectedIsUsed}
                setSelectedMenu={(opt) => {
                  setSelectedIsUsed(opt);
                  setCurrentPage(1);
                }}
              />
            </div> */}
            <div className="w-full sm:w-[80px]">
              <SelectMenu
                menuList={PER_PAGE}
                showLabel={false}
                defaultSelected={selectedPerPage}
                setSelectedMenu={(opt) => {
                  setSelectedPerPage(opt);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
          <div className="flex items-center justify-end mb-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="btn-primary float-right"
            >
              <UserPlusIcon className="h-4 w-4" />
              Add Users
            </button>
          </div>
        </div>

        <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={tableData}
          loader={usersLoading}
          name="gourmet_users_table"
          bottomBorder={totalCount > selectedPerPage?.value}
        />
        </TableCard>

        
      </div>

      <GourmetSocietyAddUsersModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false);
          fetchUsers();
          setCurrentPage(1);
        }}
      />

      {/* Need Help dialog */}
      {helpModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeHelpModal} />
          <div className="relative w-full max-w-md bg-white dark:bg-shoorah-darkBgTabColor rounded-2xl shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Need Help</h2>
            {helpModal.userName && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                For:{' '}
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {helpModal.userName}
                </span>
              </p>
            )}
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Description
            </label>
            <textarea
              rows={4}
              value={helpDescription}
              onChange={(e) => setHelpDescription(e.target.value)}
              placeholder="Describe the issue or how you'd like to help this user..."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-shoorah-primary resize-none"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeHelpModal}
                className="px-4 py-2 rounded-full text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendHelp}
                disabled={helpLoading || !helpDescription.trim()}
                className="btn-primary"
              >
                {helpLoading ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceGourmetSociety;
